<template>
  <q-card
    v-if="step === 'inquire'"
    class="q-pa-lg bg-responsive-white"
    style="width: 500px;"
    data-test-id="account-register-card"
  >
    <q-form
      ref="formRef"
    >
      <div class="column q-gutter-md">
        <div
          class="col row"
        >
          <div class="col text-h6 q-mb-md">
            {{ $t('auth.actions.register') }}
          </div>
        </div>
        <div
          class="col"
        >
          <div class="col q-mb-md">
            {{ $t('auth.areYouAnExistingCustomerQuestion') }}
          </div>
        </div>
        <div
          class="col row"
        >
          <q-btn
            class="full-width"
            size="lg"
            color="accent"
            :text-color="buttonTextColor(currentSite.accentColor)"
            :label="$t('person.newCustomer')"
            data-test-id="register-new-customer-button"
            @click="step='register'"
          />
          <div class="text-body text-center">
            {{ $t('auth.noAccountPromptToRegister') }}
          </div>
        </div>
        <div
          class="col row"
        >
          <q-btn
            class="full-width"
            size="lg"
            color="primary"
            :text-color="buttonTextColor(currentSite.primaryColor)"
            :label="$t('person.existingCustomer')"
            to="/password/claim-account"
            data-test-id="register-existing-customer-button"
          />
          <div class="text-body text-center">
            {{ $t('auth.existingAccountPromptToClaim') }}.
          </div>
        </div>
        <div
          class="col row"
        >
          <q-btn
            class="full-width"
            size="lg"
            color="secondary"
            :text-color="buttonTextColor(currentSite.secondaryColor)"
            :label="$t('person.existingCustomerWithoutEmail')"
            data-test-id="register-claim-no-email-button"
            @click="showWithoutEmailDialog = true"
          />
          <div class="text-body text-center">
            {{ $t('auth.existingAccountPromptToClaimWithoutEmail') }}.
          </div>
        </div>
      </div>
      <div
        class="row justify-between items-center q-mt-xl"
      >
        <div>
          {{ $t('auth.alreadyHaveAnAccountQuestion') }}
        </div>
        <q-btn
          flat
          color="primary"
          :label="$t('auth.actions.login')"
          data-test-id="login-link"
          @click="$kiosk && $emit('login') || $router.push('/login')"
        />
      </div>
    </q-form>
  </q-card>
  <component
    :is="displayInCard ? 'q-card' : 'div'"
    v-else-if="step === 'register'"
    class="q-pa-lg bg-responsive-white"
    style="width: 500px;"
  >
    <q-form
      ref="formRef"
    >
      <div class="column q-gutter-sm">
        <div
          v-if="showTitle"
          class="col row"
        >
          <div class="col text-h6 q-mb-md">
            {{ $t('auth.actions.register') }}
          </div>
        </div>
        <div
          class="col"
        >
          <div class="row q-gutter-md">
            <q-input
              v-model.trim="user.firstName"
              class="col"
              type="text"
              :label="$t('form.labels.firstName')"
              :rules="[
                val => required.$validator(val) || $t('form.errors.required'),
              ]"
              bottom-slots
              required
              :autofocus="!user.firstName"
              data-test-id="first-name-input"
            />
            <q-input
              v-model.trim="user.lastName"
              class="col"
              type="text"
              :label="$t('form.labels.lastName')"
              :rules="[
                val => required.$validator(val) || $t('form.errors.required'),
              ]"
              bottom-slots
              required
              :autofocus="!!user.firstName && !user.lastName"
              data-test-id="last-name-input"
            />
          </div>
        </div>
        <div
          class="col row"
        >
          <q-input
            id="email"
            v-model.trim="user.email"
            class="col-12"
            type="email"
            :label="$t('form.labels.emailAddress')"
            bottom-slots
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
              val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
            ]"
            lazy-rules
            :autofocus="!!user.firstName && !!user.lastName && !user.email"
            color="grey"
            data-test-id="email-input"
          />
        </div>
        <div
          class="col row"
        >
          <q-input
            id="password"
            v-model="user.password"
            class="col"
            type="password"
            :label="$t('form.labels.password')"
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
              val => minLength(minPasswordLength).$validator(val) || $t('form.errors.passwordTooShort')
            ]"
            lazy-rules
            bottom-slots
            :autofocus="!!user.firstName && !!user.lastName && !!user.email"
            data-test-id="password-input"
          />
        </div>
        <div
          v-if="!skipPasswordRepeat"
          class="col row"
        >
          <q-input
            id="repeatPassword"
            v-model="repeatPassword"
            class="col"
            type="password"
            :label="$t('form.labels.repeatPassword')"
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
              val => minLength(minPasswordLength).$validator(val) || $t('form.errors.passwordTooShort'),
              val => user.password === val || $t('form.errors.passwordsDoNotMatch'),
            ]"
            lazy-rules
            bottom-slots
            data-test-id="password-repeat-input"
          />
        </div>

        <transition
          leave-active-class="animated fadeOut"
          enter-active-class="animated fadeIn"
        >
          <div
            v-if="showAdditionalFields"
            class="col"
          >
            <div
              class="row q-gutter-md"
            >
              <iso-date-input
                v-if="currentCircuit.registrationOptions.prompt_for_date_of_birth"
                v-model="user.dateOfBirth"
                emit-string="nullify-blank"
                class="col"
                color="grey"
                :label="$t('form.labels.dateOfBirth')"
                bottom-slots
                :rules="[
                  val => !ageGate.dobRequiredForForm || !!val || $t('form.errors.required'),
                  () => !ageGate.blocks || $t('auth.errors.registrationMinimumAgeRequired', { age: ageGate.minAge }),
                ]"
              />
              <q-select
                v-if="currentCircuit.registrationOptions.prompt_for_user_class"
                ref="userClassSelect"
                v-model="user.userClass"
                class="col"
                :label="$t('user.userClass')"
                :options="userClassesForSelect"
                :display-value="user.userClass && user.userClass.name || $t('collection.none')"
                emit-value
                map-options
                bottom-slots
                color="grey"
                data-test-id="user-class-select"
                :hint="$t('auth.hint.userClass')"
                :rules="[val =>
                  !currentCircuit.registrationOptions.prompt_for_user_class ||
                  !currentCircuit.registrationOptions.require_user_class ||
                  !!val ||
                  $t('form.errors.required')
                ]"
              >
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section
                      :class="{ 'text-bold': scope.selected }"
                    >
                      <q-item-label>
                        {{ scope.opt.label }}
                      </q-item-label>
                      <q-item-label caption>
                        {{ scope.opt.description }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
            <div
              v-if="showPhoneInput"
              class="row q-mt-sm q-mb-lg"
            >
              <phone-number-input
                :phone="user.phone"
                class="col"
                :label="$t('form.labels.phoneNumber')"
                bottom-slots
                :required="currentCircuit.registrationOptions.require_phone"
                @phone-changed="handlePhoneChange"
              />
            </div>
            <div
              v-if="currentCircuit.registrationOptions.prompt_for_address && geoapifyEnabled"
              class="row q-gutter-md"
            >
              <address-input :user="user" />
            </div>
          </div>
        </transition>
        <div
          v-if="ageGate.enabled"
          class="row"
        >
          <q-field
            :model-value="hasVerifiedMinimumAge"
            class="col-12 required-checkbox-field"
            bottom-slots
            borderless
            :rules="[val => (val && ageGate.passes) || $t('auth.errors.registrationMinimumAgeRequired', { age: ageGate.minAge })]"
          >
            <template #prepend>
              <q-checkbox
                v-model="hasVerifiedMinimumAge"
                :disable="ageGate.blocks"
                data-test-id="minimum-age-checkbox"
              />
            </template>
            <template #control>
              <span
                :class="ageGate.passes ? 'cursor-pointer' : 'cursor-not-allowed'"
                @click.stop="() => ageGate.passes && (hasVerifiedMinimumAge = !hasVerifiedMinimumAge)"
              >
                {{ $t('assertion.minimumAgeAgreement', { age: ageGate.minAge }) }}
              </span>
            </template>
          </q-field>
        </div>
        <div
          v-if="!hasAcknowledgedTermsOfService"
          class="col row"
        >
          <q-field
            :model-value="user.acknowledgedTermsOfService"
            class="col-12 required-checkbox-field"
            bottom-slots
            borderless
            :rules="[val => val || $t('form.errors.termsOfServiceRequired')]"
          >
            <template #prepend>
              <q-checkbox
                v-model="user.acknowledgedTermsOfService"
                data-test-id="terms-checkbox"
              />
            </template>
            <template #control>
              <i18n-t
                keypath="assertion.tosAndPrivacyPolicyAgreement"
                class="cursor-pointer"
                scope="global"
                tag="span"
                @click="user.acknowledgedTermsOfService = !user.acknowledgedTermsOfService"
              >
                <template #termsOfService>
                  &nbsp;<a
                    href="/terms-of-service"
                    target="_blank"
                    @click.stop
                  >{{ $t('common.termsOfService') }}</a>&nbsp;
                </template>
                <template #privacyPolicy>
                  &nbsp;<a
                    href="/privacy-policy"
                    target="_blank"
                    @click.stop
                  >{{ $t('common.privacyPolicy') }}</a>&nbsp;
                </template>
              </i18n-t>
            </template>
          </q-field>
          <q-checkbox
            v-if="!currentSite.marketingPermissionsAutoOptIn"
            v-model="user.receiveMarketingEmails"
            class="col-12 q-mb-md text-responsive"
          >
            {{ $t('assertion.happyToReceiveMarketingFrom', { siteName: currentSite.name }) }}.
          </q-checkbox>
        </div>
        <div
          class="col row"
        >
          <q-btn
            class="full-width"
            size="lg"
            color="primary"
            :text-color="buttonTextColor(currentSite.primaryColor)"
            :loading="loading"
            :label="$t('auth.actions.signup')"
            data-test-id="register-submit-button"
            @click="register"
          />
        </div>
        <div
          v-if="showLogInOptionInRegisterStep"
          class="row justify-between items-center q-mt-xl"
        >
          <div>
            {{ $t('auth.alreadyHaveAnAccountQuestion') }}
          </div>
          <q-btn
            flat
            color="primary"
            :label="$t('auth.actions.login')"
            to="login"
            data-test-id="login-link"
          />
        </div>
      </div>
    </q-form>
  </component>
  <q-dialog
    v-model="showWithoutEmailDialog"
    data-test-id="register-claim-no-email-dialog"
  >
    <q-card style="min-width: 50%;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          {{ $t('auth.claimingAnAccountHeading') }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-card-section class="row justify-center">
          {{ $t('auth.claimingAccountExplanation') }}.
          <br><br>
          {{ $t('auth.claimingAccountInstructions', { siteEmail: currentSite ? currentSite.email : '', sitePhone: currentSite ? currentSite.phone : '' } ) }}.
        </q-card-section>

        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            v-close-popup
            label="Got it!"
            color="primary"
            :text-color="buttonTextColor(currentSite.primaryColor)"
            data-test-id="dialog-ok-button"
          />
        </q-card-actions>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { email, minLength, required, sameAs } from '@vuelidate/validators'
import { date, Dialog, Notify } from 'quasar'
import { geoapifyEnabled } from 'src/config'
import Model, { IsoDate } from 'src/models/model'
import User from 'src/models/user/User'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'
import AddressInput from 'src/modules/auth/components/AddressInput'
import PhoneNumberInput from 'src/modules/consumer/components/phone/PhoneNumberInput'

const minPasswordLength = 8

export default {
  name: 'Register',
  components: {
    AddressInput,
    IsoDateInput,
    PhoneNumberInput
  },
  mixins: [FormValidationMixin],
  configuration: ['currentCircuit', 'currentSite', 'userClasses'],
  props: {
    skipPasswordRepeat: {
      type: Boolean,
      required: false,
      default: false
    },
    showTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    displayInCard: {
      type: Boolean,
      required: false,
      default: true
    },
    showLogInOptionInRegisterStep: {
      type: Boolean,
      required: false,
      default: true
    },
    hasAcknowledgedTermsOfService: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['account-created', 'login'],
  data () {
    return {
      user: new User(),
      step: 'inquire',
      repeatPassword: '',
      showWithoutEmailDialog: false,
      loading: false,
      hasVerifiedMinimumAge: false
    }
  },
  computed: {
    isMinimumAgeRequired () {
      return this.currentSite.accountOptions.require_minimum_age &&
      _.isInteger(this.currentSite.accountOptions.minimum_age)
    },
    userAge () {
      if (!this.user.dateOfBirth) return null
      const birthdate = date.extractDate(this.user.dateOfBirth, 'YYYY-MM-DD')
      const today = new Date()

      return date.getDateDiff(today, birthdate, 'years')
    },
    ageGate () {
      const minAge = this.currentSite.accountOptions.minimum_age
      const requireMin = !!this.currentSite.accountOptions.require_minimum_age && _.isInteger(minAge)
      const checkOnReg = !!this.currentSite.accountOptions.check_age_on_registration
      const promptDob = !!this.currentCircuit.registrationOptions.prompt_for_date_of_birth
      const requireDob = !!this.currentCircuit.registrationOptions.require_date_of_birth

      const enabled = requireMin

      const hasDob = !!this.user.dateOfBirth
      const canEvaluate = enabled && promptDob && hasDob && _.isInteger(this.userAge) && checkOnReg

      const passes = !enabled ? true : (canEvaluate ? this.userAge >= minAge : !requireDob)
      const blocks = canEvaluate && this.userAge < minAge
      const dobRequiredForForm = enabled && promptDob && requireDob

      return { enabled, dobRequiredForForm, canEvaluate, passes, blocks, minAge }
    },
    previousRouteInfo () {
      try {
        const stored = sessionStorage.getItem('registerFromRoute')
        return stored ? JSON.parse(stored) : null
      } catch (e) {
        console.warn('Failed to parse stored route info:', e)
        return null
      }
    },
    isMembershipCheckoutSignupProcess () {
      return this.previousRouteInfo?.name === 'checkout:modify-membership'
    },
    showPhoneInput () {
      // If the user is signing up for a membership, we need to check if the phone is required for both membership signup and order purchase
      return this.isMembershipCheckoutSignupProcess
        ? this.currentSite.anonymousOrderRequirements.consumer_prompt_for_phone || this.currentCircuit.registrationOptions.prompt_for_phone
        : this.currentCircuit.registrationOptions.prompt_for_phone
    },
    phoneRequired () {
      // If the user is signing up for a membership, we need to check if the phone is required for both membership signup and order purchase
      return this.isMembershipCheckoutSignupProcess
        ? this.currentSite.anonymousOrderRequirements.consumer_phone_required || this.currentCircuit.registrationOptions.require_phone
        : this.currentCircuit.registrationOptions.require_phone
    },
    userClassesForSelect () {
      const userClasses = (this.$consumer || this.$kiosk) ? _.filter(this.userClasses.data, 'availableOnline') : this.userClasses.data
      return _.map(userClasses, userClass => {
        return {
          value: userClass,
          label: userClass.name,
          description: userClass.description || userClass.ageDescription,
          disable: false
        }
      })
    },
    showAdditionalFields () {
      return this.user.firstName?.length &&
        this.user.lastName?.length &&
        this.user.email?.length &&
        this.user.password?.length &&
        (this.repeatPassword.length || this.skipPasswordRepeat) && (
        this.currentCircuit.registrationOptions.prompt_for_date_of_birth ||
          this.currentCircuit.registrationOptions.prompt_for_user_class ||
          this.currentCircuit.registrationOptions.prompt_for_address ||
          this.currentCircuit.registrationOptions.prompt_for_phone ||
          this.currentSite.anonymousOrderRequirements.consumer_prompt_for_phone
      )
    }
  },
  watch: {
    'ageGate.passes' () {
      if (!this.ageGate.passes) this.hasVerifiedMinimumAge = false
    },
    'user.dateOfBirth': {
      handler () { this.validateUserClasses() },
      immediate: true
    }
  },
  created () {
    _.extend(this, { email, IsoDate, required, sameAs, minLength, minPasswordLength, geoapifyEnabled })

    this.setUserData()
  },
  methods: {
    register () {
      this.validateForm(this.registerUser, null)
    },
    async registerUser () {
      try {
        this.loading = true

        if (this.hasAcknowledgedTermsOfService) {
          this.user.acknowledgedTermsOfService = true
        }

        const { data, errors } = await this.user.register()

        if (!_.isEmpty(errors) || !_.isEmpty(data?.errors)) {
          this.loading = false

          if (_.first(errors)?.code === 'no_password_set') {
            Dialog.create({
              title: 'Account Exists',
              message: 'Your account exists but no password has been set. We will send you an email you can use to set your password.'
            }).onDismiss(() => {
              this.$router.push('/password/claim-account')
            })

            return
          } else if (_.first(errors)?.code === 'email_already_exists') {
            Dialog.create({
              title: 'Account Exists',
              message: 'You already have an account with us. Please log in or reset your password.'
            }).onDismiss(() => {
              if (this.$kiosk) {
                this.$emit('login')
              } else {
                this.$router.push('/login')
              }
            })

            return
          } else {
            this.$mh.showErrors(errors || data?.errors)

            return
          }
        }

        if (_.isNil(data?.user)) {
          this.loading = false
        } else if (this.$kiosk) {
          this.$emit('account-created', data.user.id)
          this.loading = false
        } else if (!_.isNil(data?.user)) {
          this.$emit('account-created', data.user.id)

          await Model.configuration.validate()
          await Model.context.fetch(true)

          this.$auth.navigatePostAuth('/')
          this.loading = false

          // Clear stored route information after successful registration
          this.clearStoredRouteInfo()

          Notify.create({
            message: 'Account created',
            type: 'positive',
            position: 'top',
            attrs: { 'data-test-id': 'account-created-notification' }
          })
        }
      } finally {
        this.loading = false
      }
    },
    validateUserClasses () {
      if (!this.user.dateOfBirth || this.user.dateOfBirth.substr(0, 4) === '0000') {
        if (this.user.userClass.isLocal) this.user.userClass = this.userClassesForSelect[0].value
        this.userClassesForSelect.forEach(userClassOption => { userClassOption.disable = false })
        return
      }

      // Make options available based on age
      const birthdate = date.extractDate(this.user.dateOfBirth, 'YYYY-MM-DD')
      const today = new Date()

      const age = Math.floor(date.getDateDiff(today, birthdate, 'days') / 365)

      if (age < 0 || age > 200) return

      let firstMatch = null
      this.userClassesForSelect.forEach(userClassOption => {
        const min = userClassOption.value.minAge || 0
        const max = userClassOption.value.maxAge || 200

        const match = (age >= min && age <= max)

        userClassOption.disable = !match

        if (!firstMatch && !userClassOption.disable) firstMatch = userClassOption.value
      })

      if (firstMatch) this.user.userClass = firstMatch
    },
    buttonTextColor (color) {
      return this.$isColorDark(color) ? 'white' : 'black'
    },
    handlePhoneChange (phoneObject) {
      if (phoneObject?.isValid?.()) {
        this.user.phone = phoneObject.nationalNumber
        this.user.phoneCountryCode = phoneObject.country
      } else {
        this.user.phone = null
        this.user.phoneCountryCode = null
      }
    },
    setUserData () {
      const authData = this.$auth.getAuthData()
      if (authData.email) {
        this.user.email = authData.email
        this.step = 'register'
      }
      if (authData.firstName) {
        this.user.firstName = authData.firstName
      }
      if (authData.lastName) {
        this.user.lastName = authData.lastName
      }
      if (this.currentSite.marketingPermissionsAutoOptIn || this.currentSite.marketingPermissionsAutoSelect) {
        this.user.receiveMarketingEmails = true
      }
    }
  }
}
</script>

<style scoped>
.required-checkbox-field :deep(.q-field__prepend) {
  padding-right: 0;
}
.required-checkbox-field :deep(.q-field__marginal),
.required-checkbox-field :deep(.q-field__control),
.required-checkbox-field :deep(.q-field__native) {
  height: auto;
  min-height: auto;
}
</style>
